import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useQueryParam, StringParam } from 'use-query-params';
import Spinner from '../../../components/react/spinner/spinner';
import { Navigate } from 'react-router';
import { unsubscribeFromNewsletter } from '../../../services/newsletter/newsletter.api.service';
import './unsubscribe_newsletter.scss';

const UnsubscribeNewsletter = ({ newsletter }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setErrorMessage] = useState(false);

  // Email comes as url param, ex: ?email=a@b.com OR (encoded) ?email=a%40b.com
  const [emailDecoded] = useQueryParam('email', StringParam);

  // params are not needed in the referrer. Ex: http://www.devex.com/newsletters#newswire
  const originAndPathName = window.location.origin + window.location.pathname;
  const referrer = `${originAndPathName}#${newsletter.permalink}`;

  useEffect(() => {
    if (emailDecoded) {
      setIsLoading(true);

      unsubscribeFromNewsletter(newsletter.id, {email: emailDecoded, referrer})
        .then(() => {
          setShowSuccessMessage(true);
          setErrorMessage(false);
        })
        .catch((error) => {
          if (error.message === '405 Not Allowed') {
            // This error occurs when the user is already unsubscribed, so we treat it like a
            // successful operation...
            setShowSuccessMessage(true);
            setErrorMessage(false);
          } else {
            setShowSuccessMessage(false);
            setErrorMessage(true);
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [emailDecoded]);

  if (!emailDecoded) {
    return <Navigate replace to={`/newsletters/${newsletter.permalink}`} />;
  }

  return (
    <div data-testid='unsubscribe-newsletter' className='unsubscribe-newsletter'>
      {showSuccessMessage && (
        <div className='unsubscribe-successful' data-testid='unsubscribe-successful'>
          <i className='fal icon-check-circle text-success'></i>
          <h3 className='mb-3'>Sorry to see you go!</h3>
          <p className='text-gray'>
            Your email <b>{emailDecoded}</b>{' '}
            has been unsubscribed from the <b>{newsletter.name}</b>{' '}
            newsletter.
          </p>
        </div>
      )}
      {showErrorMessage && (
        <div className='unsubscribe-unsuccessful' data-testid='unsubscribe-unsuccessful'>
          <i className='fal icon-exclamation-circle text-danger'></i>
          <h3 className='mb-3'>Something went wrong!</h3>
          <p className='text-gray'>
            There was an error unsubscribing your email <b>{emailDecoded}</b> from the{' '}
            <b>{newsletter.name}</b> newsletter.
          </p>
          <p>
            Please try again or <a href='mailto:info@devex.com'>contact support</a>.
          </p>
        </div>
      )}
      <Spinner isLoading={isLoading} isAbsolute={true}></Spinner>
    </div>
  );
};

UnsubscribeNewsletter.propTypes = {
  newsletter: PropTypes.shape({
    id: PropTypes.number.isRequired,
    permalink: PropTypes.string.isRequired,
    name: PropTypes.string,
  })
};

export default UnsubscribeNewsletter;
